import { UserPlus } from 'lucide-react';
import { useMemo } from 'react';
import {
  useFieldValue,
  useGetOne,
  useGetResourceLabel,
  useTranslate,
} from 'ra-core';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../../components/admin/data-table';
import { DateField } from '../../../components/admin/date-field';
import { Error as ErrorPage } from '../../../components/admin/error';
import { Loading } from '../../../components/admin/loading';
import {
  NumberField,
  type NumberFieldProps,
} from '../../../components/admin/number-field';
import { AssignmentEmpty } from '../../Moonstar/AssignmentEmpty';
import { MoonstarList } from '../../Moonstar/MoonstarList';
import { BulkRemoveKpiAssignees } from '../KpiAssignee/BulkRemoveKpiAssignee';
import { type KpiResponseDto } from '../types';
import { KpiDataActions } from './KpiDataActions';
import { kpiDataExporter } from './kpiDataExporter';
import { KpiPerformanceBadge } from './KpiPerformanceBadge';

export const KpiDataList = () => {
  const { kpiId } = useParams<{ kpiId: string }>();
  const numericKpiId = Number(kpiId);
  const kpi = useGetOne<KpiResponseDto>('kpis', { id: numericKpiId });
  const translate = useTranslate();
  const getResourceLabel = useGetResourceLabel();
  const kpiExporter = useMemo(
    () => (kpi.data ? kpiDataExporter(kpi.data) : undefined),
    [kpi.data]
  );

  if (kpi.isPending) {
    return <Loading />;
  } else if (kpi.isError) {
    return (
      <ErrorPage
        resetErrorBoundary={() => void kpi.refetch()}
        error={kpi.error}
      />
    );
  }

  return (
    <MoonstarList
      empty={
        <AssignmentEmpty
          message="moonstar.kpiAssignees.noAssignees"
          to={`/kpis/${kpi.data.id}/assignees/add`}
          icon={<UserPlus />}
          label="moonstar.kpiAssignees.addAssignees"
        />
      }
      actions={<KpiDataActions kpi={kpi.data} />}
      filter={{
        kpiId: numericKpiId,
      }}
      exporter={kpiExporter}
      resource="kpi-data"
      sort={{ field: 'assigneeUnitName', order: 'ASC' }}
      title={[
        { label: getResourceLabel('kpis', 2), href: '/kpis' },
        kpi.data.name,
        translate('moonstar.kpiData.title'),
      ]}
    >
      <DataTable
        bulkActionButtons={<BulkRemoveKpiAssignees kpiId={kpi.data.id} />}
        rowClick={false}
      >
        <DataTable.Col source="assigneeUnitName" />
        <DataTable.NumberCol source="latestActual" />
        <DataTable.NumberCol source="latestTarget" />
        <DataTable.Col source="performance" disableSort>
          <KpiPerformanceField source="performance" />
        </DataTable.Col>
        <DataTable.NumberCol source="latestScore" />
        <DataTable.Col source="lastUpdated">
          <DateField
            source="lastUpdated"
            empty="moonstar.kpiData.lastUpdatedNever"
          />
        </DataTable.Col>
      </DataTable>
    </MoonstarList>
  );
};

const KpiPerformanceField = (props: NumberFieldProps) => {
  const recordValue = useFieldValue(props);
  const value = Number(recordValue);

  if (recordValue === null || isNaN(value) || !isFinite(value)) {
    return <NumberField {...props} />;
  }

  return <KpiPerformanceBadge value={value} />;
};
